import React from "react";

/**
 * Logo EMMPIEZA — theme-aware
 * ───────────────────────────
 * Réplica vectorial del logo original (assets/images/logo.png): tres
 * plataformas 3D ascendentes + wordmark "emmpieza".
 *
 * • Wordmark: `fill="currentColor"` hereda el color del contexto.
 *   → grafito (#17171a) en light, blanco-crema (#f4f4f0) en dark.
 *   → Por eso debe envolverse en un contenedor con la clase `text-em-text`.
 * • Plataformas: cada losa es un contorno redondeado relleno con
 *   `var(--em-vivid)` → `var(--em-vivid-2)`, más una "costura" trazada con
 *   `var(--em-bg)` que separa la cara superior del canto — el mismo truco
 *   de línea recortada que usa el PNG original para el efecto 3D.
 *
 * No usa hex fijos — todo viene de los tokens de `src/styles/theme.css`.
 */

/* Losa en coordenadas locales (~134×34): contorno y costura del canto */
const SLAB_OUTLINE =
    "M 8 0.3 L 82 0 Q 87.5 0 91.5 2.3 L 130.5 22.5 Q 134.6 24.7 134.3 27.4 " +
    "L 133.9 29 Q 133.5 31.5 128.6 31.7 L 36 33.6 Q 29.4 33.9 25.2 30.2 " +
    "L 4.5 13.8 Q 0.4 10.4 1.6 7.2 Q 3 3 8 0.3 Z";
const SLAB_SEAM =
    "M 3.4 3.2 L 28.3 22 Q 30.1 23.2 32.3 23.1 L 129.3 24.8";

/* Escalera ascendente: inferior-izquierda → media → superior-derecha */
const SLAB_OFFSETS = [
    [1, 68],
    [93, 35],
    [185, 2],
];

function Logo({
    className = "",
    ariaLabel = "EMMPIEZA — tecnología y ciberseguridad",
    title = "EMMPIEZA",
}) {
    const uid = React.useId().replace(/[^a-zA-Z0-9_-]/g, "");
    const gradId = `logoShapeGrad-${uid}`;

    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 320 200"
            role="img"
            aria-label={ariaLabel}
            className={`block ${className}`}
        >
            <title>{title}</title>
            <defs>
                <linearGradient id={gradId} x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop
                        offset="0%"
                        style={{ stopColor: "var(--em-vivid)", stopOpacity: 1 }}
                    />
                    <stop
                        offset="100%"
                        style={{ stopColor: "var(--em-vivid-2)", stopOpacity: 1 }}
                    />
                </linearGradient>
            </defs>

            {SLAB_OFFSETS.map(([x, y]) => (
                <g key={`${x}-${y}`} transform={`translate(${x} ${y})`}>
                    <path d={SLAB_OUTLINE} fill={`url(#${gradId})`} />
                    {/* Costura color-fondo: simula el canto de la losa */}
                    <path
                        d={SLAB_SEAM}
                        fill="none"
                        stroke="var(--em-bg)"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </g>
            ))}

            {/* Wordmark — hereda color del padre vía currentColor */}
            <text
                x="160"
                y="170"
                textAnchor="middle"
                fontFamily="var(--em-font, 'Inter'), system-ui, sans-serif"
                fontSize="54"
                fontWeight="800"
                letterSpacing="-0.02em"
                fill="currentColor"
            >
                emmpieza
            </text>
        </svg>
    );
}

export default Logo;
